// Per-machine effective roles (#6757, epic #6552 "roles on machines") — for one machine,
// which roles it runs and why a role it is not running is blocked, and per role how many
// machines run it. Pure: the rows are read in `@developerz/db` and handed in; the two
// doors are `GET /v1/fleet/utilization` (each machine row's `roles`) and
// `GET /v1/agents/activity` (each agent's `machines`), both in `apps/api`.
//
// A DISPLAY PROJECTION, not the decision. Whether a task is claimed on a box is decided by
// the claim (`@developerz/db` `queries/tasks/`: the machine conjunct `machineRoleOffSql`,
// the mode gate, `interactiveOriginsAllowed` and the capability filter
// `modeNarrowedRequiresSql` feeds). This module restates, for the Fleet and Agents pages,
// the three per-machine facts those read, through the same domain predicates:
//
//   - the org's on/off for the mode (`orgAgentModeFlags`; repo overrides are per repo and
//     are not projected onto a machine),
//   - whether the box's pinned role can hold an interactive session
//     (`runnerRoleCanConverse`), and
//   - whether its reported build applies the org's tool narrowing
//     (`boxModeNarrowingCapable`, the build the claim's `modeNarrowedRequiresSql` needs), and,
//     for Gardener, whether it carries the model-call executor (`boxModelCallCapable`, #6763),
//     and for Planner, whether it registers the planner schema (`boxPlannerCapable`, #6765).
//
// The machine switch is ANDed over all of them: `effective` is `machine_enabled` AND no
// blocked reason, and a machine row never re-enables an org's "off".

import { type AgentModeKey, agentModeWritable } from '../entities/agent-mode';
import {
  boxModelCallCapable,
  boxModeNarrowingCapable,
  boxPlannerCapable,
} from '../entities/runner/box-kek';
import { type RunnerRole, runnerRoleCanConverse } from '../entities/runner/role';
import type { RunnerOwnerKind } from '../entities/runner/status';
import { AGENT_MODE_KEY_FOR_ORIGIN } from './agent-roster';
import { INTERACTIVE_TASK_ORIGINS, type TaskOrigin } from './task-envelope';

/**
 * The card's order (`docs/design/fleet-redesign/prototype.html` `CAPS`): Coder (`worker`),
 * Reviewer, CI Fixer, Planner, Scout, Triage, DevOps, Gardener, Escalation. Every stored
 * key exactly once — the rank record below is `Record<AgentModeKey, …>`, so a key added to
 * the vocabulary is a type error here until it is given a place.
 */
const CARD_RANK: Readonly<Record<AgentModeKey, number>> = {
  worker: 0,
  reviewer: 1,
  ci_fixer: 2,
  planner: 3,
  scout: 4,
  triage: 5,
  devops: 6,
  gardener: 7,
  escalation: 8,
};

/** Every stored mode key in the card's order. */
export const MACHINE_ROLE_CARD_ORDER: readonly AgentModeKey[] = (
  Object.keys(CARD_RANK) as AgentModeKey[]
).sort((a, b) => CARD_RANK[a] - CARD_RANK[b]);

/**
 * Each role's name on the machine card (#6767), the `<Role>` in Send work's refusal copy.
 * The card's names, not the dashboard's `AgentId`s (`coder`, not `worker`).
 */
export const MACHINE_ROLE_NAME: Readonly<Record<AgentModeKey, string>> = {
  worker: 'Coder',
  reviewer: 'Reviewer',
  ci_fixer: 'CI Fixer',
  planner: 'Planner',
  scout: 'Scout',
  triage: 'Triage',
  devops: 'DevOps',
  gardener: 'Gardener',
  escalation: 'Escalation',
};

/** Why a role a machine's own switch allows is still not running there; the FIRST that holds. */
export const MachineRoleBlocked = {
  /** The org resolution of the mode is off (the org row, else the code default). */
  OrgOff: 'org_off',
  /** An interactive role on a box whose pinned role cannot hold a session. */
  CannotConverse: 'cannot_converse',
  /** The role's lane needs a capability the box's REPORTED build lacks. */
  NeedsUpdate: 'needs_update',
} as const;
export type MachineRoleBlocked = (typeof MachineRoleBlocked)[keyof typeof MachineRoleBlocked];

/** One role on one machine, as the Fleet page's card reads it. */
export type MachineRoleView = {
  readonly key: AgentModeKey;
  /** False exactly when the machine's own switch for the key is stored off. */
  readonly machine_enabled: boolean;
  /** `machine_enabled` AND `blocked === null`. */
  readonly effective: boolean;
  readonly blocked: MachineRoleBlocked | null;
  /** Tasks this machine holds for this account whose switch is this key. */
  readonly running: number;
};

/** What the verdict reads of one machine row. */
export type MachineRoleRunner = {
  readonly ownerKind: RunnerOwnerKind;
  /** The home org's login on a cross-org row (owner ruling G23); null for the account's own. */
  readonly viaOrg: string | null;
  /** The DB `runners.mode` — the column the claim reads, a placeholder until the presence sweep syncs it. */
  readonly mode: RunnerRole;
  /** The build the box LAST REPORTED live, or null when none was reported. */
  readonly runnerVersion: string | null;
};

/**
 * Whether a machine row carries switches on this account: one of its OWN ORG machines, the
 * set the machine write scope admits (`ownedOrgMachineWhere`, `@developerz/db`) and the
 * claim's machine conjunct honours. A cross-org row's switches are its own org's
 * (read-only here, G23); a user-owned box has none (a write to one answers `machine_not_found`). The one predicate both the
 * row's `roles` and the "N of M" denominator are taken over.
 */
export function machineCarriesRoles(
  runner: Pick<MachineRoleRunner, 'ownerKind' | 'viaOrg'>,
): boolean {
  return runner.ownerKind === 'org' && runner.viaOrg === null;
}

/**
 * The keys whose lane runs interactive sessions, DERIVED: a key some interactive origin
 * (`INTERACTIVE_TASK_ORIGINS`) is held under (`AGENT_MODE_KEY_FOR_ORIGIN`). `setup` is held
 * under no key, so today this is `scout` alone.
 */
const CONVERSING_KEYS: ReadonlySet<AgentModeKey> = new Set(
  INTERACTIVE_TASK_ORIGINS.map((o) => AGENT_MODE_KEY_FOR_ORIGIN[o]).filter(
    (k): k is AgentModeKey => k !== null,
  ),
);

export type MachineRoleViewsInput = {
  readonly runner: MachineRoleRunner;
  /**
   * The org-scope `enabled` per key (`orgAgentModeFlags`). A key with no org door has no
   * entry and never reads `org_off`: the machine-only keys (`triage`, `gardener`,
   * `escalation`) have no org row.
   */
  readonly orgFlags: Readonly<Partial<Record<AgentModeKey, boolean>>>;
  /**
   * The keys whose org mode narrows its tools (`code_tools` non-null). The caller passes
   * the EMPTY set for a principal without `write:prompts` (D11), so the bit never reaches
   * that principal's body.
   */
  readonly narrowedOrgKeys: ReadonlySet<AgentModeKey>;
  /** This machine's keys whose own switch is stored off (`listMachineRoleOffKeys`). */
  readonly machineOffKeys: ReadonlySet<AgentModeKey>;
  /** This machine's held tasks (`assigned` / `in_progress`, this account) per origin. */
  readonly heldOrigins: ReadonlyMap<TaskOrigin, number>;
  /** The keys a machine door may write — production passes `AGENT_MODE_WRITABLE.machine`. */
  readonly writableKeys: readonly AgentModeKey[];
};

function firstBlockedReason(
  key: AgentModeKey,
  input: MachineRoleViewsInput,
): MachineRoleBlocked | null {
  if (agentModeWritable('org', key) && input.orgFlags[key] === false) {
    return MachineRoleBlocked.OrgOff;
  }
  if (CONVERSING_KEYS.has(key) && !runnerRoleCanConverse(input.runner.mode)) {
    return MachineRoleBlocked.CannotConverse;
  }
  // A null version is an UNKNOWN build (never reported, lapsed presence, or a presence read
  // that did not answer), not a lacking one: only a reported build below the minimum holds.
  const version = input.runner.runnerVersion;
  if (input.narrowedOrgKeys.has(key) && version !== null && !boxModeNarrowingCapable(version)) {
    return MachineRoleBlocked.NeedsUpdate;
  }
  // The gardener's work on a box is a model call (#6763): a `kb_fold` offer is claimed only by
  // a build with the model-call executor (`boxModelCallCapable`, the claim's own
  // `runnerServesModelCalls`), so a REPORTED build below it cannot run the role. Same
  // null-version rule as above: unknown is not lacking.
  if (key === 'gardener' && version !== null && !boxModelCallCapable(version)) {
    return MachineRoleBlocked.NeedsUpdate;
  }
  // The Planner's work on a box is a `plan_author` model call (#6765), and a scout task is
  // claimable only by a build that derives `harness:planner` (#6764, R1:
  // `requiredCapabilitiesForOrigin(TaskOrigin.Scout)`). Both roles therefore need
  // `boxPlannerCapable`; without the `scout` arm a sub-minimum box would show Scout effective
  // while it can claim no scout task. Same null-version rule.
  if ((key === 'planner' || key === 'scout') && version !== null && !boxPlannerCapable(version)) {
    return MachineRoleBlocked.NeedsUpdate;
  }
  return null;
}

/**
 * One machine's roles: one entry per writable key, in the card's order — or `null` on a
 * row that carries no switches here ({@link machineCarriesRoles}). Pure.
 */
export function machineRoleViews(input: MachineRoleViewsInput): readonly MachineRoleView[] | null {
  if (!machineCarriesRoles(input.runner)) return null;
  const running = new Map<AgentModeKey, number>();
  for (const [origin, n] of input.heldOrigins) {
    const key = AGENT_MODE_KEY_FOR_ORIGIN[origin];
    if (key !== null) running.set(key, (running.get(key) ?? 0) + n);
  }
  return MACHINE_ROLE_CARD_ORDER.filter((k) => input.writableKeys.includes(k)).map((key) => {
    const machineEnabled = !input.machineOffKeys.has(key);
    const blocked = firstBlockedReason(key, input);
    return {
      key,
      machine_enabled: machineEnabled,
      effective: machineEnabled && blocked === null,
      blocked,
      running: running.get(key) ?? 0,
    };
  });
}

/** How many machines one role is on, over how many. */
export type RoleMachineCount = { readonly on: number; readonly total: number };

/**
 * Per writable key: `total` the machines that carry roles (non-null), `on` those of them
 * online, not drained, with the key `effective`. `null` when the presence read did not
 * answer: `online: false` there means "not consulted", so a count would read as
 * "running nowhere". Pure.
 */
export function roleMachineCounts(input: {
  readonly presence: 'ok' | 'unknown';
  readonly machines: ReadonlyArray<{
    readonly roles: readonly MachineRoleView[] | null;
    readonly online: boolean;
    readonly drained: boolean;
  }>;
  readonly writableKeys: readonly AgentModeKey[];
}): ReadonlyMap<AgentModeKey, RoleMachineCount> | null {
  if (input.presence === 'unknown') return null;
  const carrying = input.machines.filter((m) => m.roles !== null);
  const out = new Map<AgentModeKey, RoleMachineCount>();
  for (const key of MACHINE_ROLE_CARD_ORDER.filter((k) => input.writableKeys.includes(k))) {
    const on = carrying.filter(
      (m) => m.online && !m.drained && m.roles?.some((r) => r.key === key && r.effective),
    ).length;
    out.set(key, { on, total: carrying.length });
  }
  return out;
}
